import { BehaviorSubject, Observable, of, throwError as observableThrowError } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import moment from 'moment';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { ActivitiesViewType } from 'src/assets/data/enum/fieldActivities.enum';
import { throwError } from 'rxjs/internal/observable/throwError';
import { UserService } from './user.service';
import {
  Crop,
  Field,
  FieldActivity,
  FieldActivityGrouped,
  FieldActivityItem,
  User,
} from '../_models';
import { CropMetricsService } from './cropMetrics.service';
import { FieldActivityTypes } from '../../../assets/data/enum/fieldActivityTypes';
import { UnitMeasurement } from '../../../assets/data/enum/unit_measurement.enum';
import { AppState } from '../_reducers';
import { environment } from '../../../environments/environment';
import { OperationStateService } from './operation-state.service';
import { selectAllFieldActivities } from '../_selectors/fieldActivity.selectors';
import { PaginatedResponse } from '../_models/paginatedResponse';
import { XYearService } from './xYear.service';
import { TagService } from './tag.service';
import { FieldActivitiesCustomType } from '../_models/fieldActivityCustomType';
import { ExportConfig } from '../_models/export';

@Injectable()
export class FieldActivityService {
  private apiBaseUrl = environment.apiBaseUrl + 'api/private/v1/';
  private apiUrl = environment.apiBaseUrl;
  private activityServiceUrl = environment.activityServiceUrl + 'api/private/v2/cache/';
  private unitMeasurement = UnitMeasurement;
  fieldActivityList: Observable<FieldActivity[]>;
  selectedActivitiesViewType$: BehaviorSubject<ActivitiesViewType> =
    new BehaviorSubject<ActivitiesViewType>(ActivitiesViewType.GROUPED);
  resultList$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<Array<FieldActivity>>(
    []
  );
  originalFieldActivityList$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<
    Array<FieldActivity>
  >([]);
  currentUser: User = new User();
  allFieldActivity$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<
    Array<FieldActivity>
  >(null);
  lastFieldActivity$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<
    Array<FieldActivity>
  >([]);
  fieldActivitiesCustomType$: BehaviorSubject<FieldActivitiesCustomType[]> = new BehaviorSubject<
    FieldActivitiesCustomType[]
  >([]);
  countCallFa = 0;
  cropResiduesDestinations = [
    'removed_from_field_for_use',
    'removed_from_field_for_sale',
    'left_on_field',
    'burned_in_field',
  ];

  constructor(
    private http: HttpClient,
    private jwtService: OperationStateService,
    private store: Store<AppState>,
    private cropMetricsService: CropMetricsService,
    private userService: UserService,
    private xYearService: XYearService,
    private tagService: TagService
  ) {
    if (environment.local) {
      // pointing to the cache disabled to point to the monolith directly with api/private/v1
      this.activityServiceUrl = environment.apiBaseUrl + 'api/private/v1/';
    }
    this.fieldActivityList = this.store.pipe(select(selectAllFieldActivities));
    this.fieldActivityList.subscribe((res) => {
      this.originalFieldActivityList$.next(res);
    });

    this.userService.getUserMeFromLocalStore().subscribe((u: User) => {
      this.currentUser = u;
    });
  }

  getFromLocalStore(): Observable<FieldActivity[]> {
    return this.fieldActivityList;
  }

  getFA() {
    return this.originalFieldActivityList$;
  }

  setSelectedActivitiesViewType(viewType: ActivitiesViewType) {
    this.selectedActivitiesViewType$.next(viewType);
  }

  getSelectedActivitiesViewType(): Observable<ActivitiesViewType> {
    return this.selectedActivitiesViewType$.asObservable();
  }

  getFieldActivities(
    selectedYear: number,
    forceBaseUrl = null,
    tags?: string
  ): Observable<FieldActivity[]> {
    const year = selectedYear ?? this.xYearService.getYear();
    const tagQuery = this.tagService.getTagQuery();

    // Base URL handling
    const hasForcedBaseUrl = !!forceBaseUrl;
    this.activityServiceUrl = hasForcedBaseUrl ? forceBaseUrl : this.activityServiceUrl;
    const fallBackActivityCheck = !hasForcedBaseUrl;

    // User settings handling
    const { activityPersonalInfo, activityPersonalInfoEmployee } =
      this.currentUser?.userSettings ?? {};
    const onlyPersonalActivity = activityPersonalInfoEmployee ?? activityPersonalInfo ?? false;

    // Excludes query building
    const excludesList = ['machineActivity', 'machineActivities', 'equipmentActivities'];
    const excludesAppend = excludesList.map((item) => `&excludes=${item}`).join('');

    return this.http
      .get<
        FieldActivity[]
      >(this.activityServiceUrl + 'fieldActivities?onlyPersonalActivity=' + onlyPersonalActivity + excludesAppend + '&cropYear=' + year + tagQuery)
      .pipe(
        tap((response) => {
          this.allFieldActivity$.next(response);
        }),
        catchError((error: HttpErrorResponse) => {
          const shouldFallback =
            [400, 403, 504].includes(error.status) &&
            fallBackActivityCheck &&
            this.countCallFa === 0;

          if (shouldFallback) {
            this.jwtService.enabledMessageAlertAPI$.next(false);
            this.countCallFa++;
            return this.getFieldActivities(year, this.apiBaseUrl, tags);
          }

          return observableThrowError(() => error);
        })
      );
  }

  getFieldActivitiesWithPagination(
    selectedYear: number = null,
    all: boolean = null,
    max: number = null,
    offset: number = null,
    sort: string = null,
    order: string = null
  ): Observable<FieldActivity[]> {
    let onlyPersonalActivity = 'false';
    if (this.currentUser && this.currentUser.userSettings) {
      if (this.currentUser.userSettings.activityPersonalInfo !== undefined) {
        onlyPersonalActivity = this.currentUser.userSettings.activityPersonalInfo.toString();
      }
    }

    let params = new HttpParams()
      .set('onlyPersonalActivity', onlyPersonalActivity)
      .set('cropYear', selectedYear?.toString() || '')
      .append('excludes', 'machineActivity')
      .append('excludes', 'machineActivities')
      .append('excludes', 'equipmentActivities');

    if (all != null) {
      params = params.set('all', all.toString());
    }
    if (max != null) {
      params = params.set('max', max.toString());
    }
    if (offset != null) {
      params = params.set('offset', offset.toString());
    }
    if (sort != null) {
      params = params.set('sort', sort);
    }
    if (order != null) {
      params = params.set('order', order);
    }

    return this.http
      .get<FieldActivity[]>(this.activityServiceUrl + 'fieldActivities', { params })
      .pipe(
        map((response) => {
          this.lastFieldActivity$.next(response);
          return response;
        })
      );
  }

  getFieldActivity(id: number): Observable<FieldActivity> {
    return this.http
      .get<FieldActivity>(this.apiBaseUrl + 'fieldActivities/' + id)
      .pipe(map((response) => response));
  }

  saveFieldActivity(fieldActivityItem): Observable<FieldActivity> {
    const fieldActivity = { ...fieldActivityItem };
    delete fieldActivity.lastUpdated;
    delete fieldActivity.dateCreated;
    delete fieldActivity.cropObj;
    delete fieldActivity.verificationDate;
    delete fieldActivity.machineActivity;
    delete fieldActivity.machineActivities;

    // Normalize mixes
    if (Array.isArray(fieldActivity.mixes)) {
      fieldActivity.mixes = fieldActivity.mixes.map((mix) => ({
        ...mix,
        product: mix.product ? { id: mix.product.id } : mix.product,
      }));
    }

    // Normalize equipments
    if (Array.isArray(fieldActivity.equipments)) {
      fieldActivity.equipments = fieldActivity.equipments.map((equipment) => ({
        id: equipment.id,
      }));
    }

    // Normalize users
    if (Array.isArray(fieldActivity.users)) {
      fieldActivity.users = fieldActivity.users.map((user) => ({
        id: user.id,
      }));
    }

    // Normalize field
    if (fieldActivity.field) {
      fieldActivity.field = { id: fieldActivity.field.id };
    }

    // Attach companyId
    const companySelected = JSON.parse(localStorage.getItem('companySelected') ?? 'null');
    fieldActivity.companyId = companySelected?.id ?? null;

    // Choose PUT or POST based on the presence of the entity id
    const request$ = fieldActivity.id
      ? this.http.put<FieldActivity>(
          `${this.apiBaseUrl}fieldActivities/${fieldActivity.id}`,
          fieldActivity
        )
      : this.http.post<FieldActivity>(`${this.apiBaseUrl}fieldActivities/`, fieldActivity);
    return request$.pipe(
      // Trigger a side effect after a successful save without altering the stream
      tap(() => {
        this.cropMetricsService.getCropMetrics();
      })
    );
  }

  deleteFieldActivity(id: number): Observable<FieldActivity> {
    return this.http.delete<void>(`${this.apiBaseUrl}fieldActivities/${id}`).pipe(
      tap(() => {
        this.cropMetricsService.getCropMetrics().subscribe();
      }),
      map(() => ({ id }) as FieldActivity)
    );
  }

  getLocalFieldActivityById = (id): FieldActivity => {
    const list = this.originalFieldActivityList$.value;
    const item = list?.find((i) => i.id === id);

    return item ? structuredClone(item) : null;
  };

  async getLocalFieldActivityByUUID(uuid: string): Promise<Array<FieldActivity>> {
    if (!uuid) {
      return [];
    }

    const fas = await firstValueFrom(
      this.fieldActivityList.pipe(first((list: FieldActivity[]) => list && list.length > 0))
    );

    return fas.filter((f) => f.uuid === uuid);
  }

  async getLocalFieldActivityDone(id: number): Promise<FieldActivity[]> {
    if (!id) {
      return [];
    }

    const fas = await firstValueFrom(this.fieldActivityList);

    return fas.filter((fa) => fa?.field?.id === id && fa.done !== false);
  }

  getLocal2FieldActivityById = (id: number): FieldActivity => {
    let item: FieldActivity;
    this.originalFieldActivityList$
      .pipe(first((list: FieldActivity[]) => list && list.length > 0))
      .subscribe((list: FieldActivity[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  setFilteredFaByIdByObservablelist(faIds: number[]): void {
    const result = faIds
      .map((id) => this.getLocalFieldActivityById(id))
      .filter((fa): fa is FieldActivity => !!fa);

    this.resultList$.next(result);
  }

  getFilteredFaByIdByObservablelist() {
    return this.resultList$.asObservable();
  }

  getUnitMeasurementConfiguration() {
    return [this.unitMeasurement.KG_HA, this.unitMeasurement.L_HA, this.unitMeasurement.UNIT];
  }

  getUnitMeasurementConfigurationSeeding() {
    return [this.unitMeasurement.KG_HA, this.unitMeasurement.UNIT];
  }

  getUnitMeasurementConfigurationIrrigating() {
    return [
      this.unitMeasurement.L_S,
      this.unitMeasurement.MQ_H,
      this.unitMeasurement.L_MIN,
      this.unitMeasurement.L_H,
      this.unitMeasurement.MM,
    ];
  }

  getUnitMeasurementConfigurationFieldSubmersion() {
    return [this.unitMeasurement.L, this.unitMeasurement.MC];
  }

  getUnitMeasurementConfigurationDeposit(): string[] {
    return [this.unitMeasurement.KG, this.unitMeasurement.L, this.unitMeasurement.BU];
  }

  async retrieveLastDoneActivityFromField(field: Field): Promise<FieldActivity | null> {
    const activities = await this.getLocalFieldActivityDone(field.id);
    if (!activities?.length) {
      return null;
    }

    let latest = activities[0];
    let latestTime = new Date(latest.activityDate).getTime();

    for (let i = 1; i < activities.length; i++) {
      const currentTime = new Date(activities[i].activityDate).getTime();
      if (currentTime > latestTime) {
        latest = activities[i];
        latestTime = currentTime;
      }
    }

    return latest || null;
  }

  getExportType(): ExportConfig[] {
    return [
      {
        type: 'FieldActivityList',
        subtype: 'FieldActivityList',
        formats: [
          {
            type: 'csv',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'WorkPlan',
        formats: [
          {
            type: 'xlsx',
            enabled: true,
            compression: false,
            col: '12',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'CultivationPlan',
        formats: [
          {
            type: 'csv',
            enabled: false,
            compression: true,
            col: '4',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '4',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '4',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'Treatments',
        formats: [
          {
            type: 'csv',
            enabled: false,
            compression: true,
            col: '4',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '4',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '4',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'PlotsAndParcels',
        formats: [
          {
            type: 'csv',
            enabled: false,
            compression: true,
            col: '4',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '4',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '4',
          },
        ],
      },
    ];
  }

  getExportTypeEquipment = (): Array<any> => {
    return [
      {
        type: 'EquipmentActivities',
        subtype: 'CompleteEquipmentActivityListToLocalExport',
        formats: [
          {
            type: 'csv',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
    ];
  };

  checkIfCropExistInFieldActivity(idCrop: number): Observable<FieldActivity[]> {
    if (!idCrop) {
      return of([]);
    }

    return this.fieldActivityList.pipe(map((list) => list.filter((f) => f.crop?.id === idCrop)));
  }

  checkFieldActivitiesByCropId(crop: Crop): Observable<any> {
    if (!crop) {
      return this.http.get<any>(`${this.apiBaseUrl}fieldActivities`);
    }

    const params: Record<string, string> = {};

    if (crop.uuid) {
      params['cropUuid'] = crop.uuid;
    } else if (crop.id) {
      params['cropId'] = String(crop.id);
    }

    return this.http.get<any>(`${this.apiBaseUrl}fieldActivities`, {
      params,
    });
  }

  checkIfCropListExistInFieldActivity(cropList: Crop[]): Observable<FieldActivity[]> {
    if (!cropList || cropList.length === 0) {
      return of([]);
    }

    const cropIds = new Set(cropList.map((crop) => crop.id));

    return this.fieldActivityList.pipe(
      map((fieldActivityList: FieldActivity[]) =>
        fieldActivityList.filter((fa) => fa.crop != null && cropIds.has(fa.crop.id))
      )
    );
  }

  getAllFieldActivityTypes = (): string[] => {
    const keys = this.getAllKeys(FieldActivityTypes).sort((a, b) => a.localeCompare(b));

    return keys.map((key) => (key.includes('.') ? key.split('.')[1] : key));
  };

  getAllKeys(obj: any): string[] {
    let keys: string[] = [];

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        keys.push(key);
        if (typeof obj[key] === 'object' && Object.keys(obj[key]).length > 0) {
          const subKeys = this.getAllKeys(obj[key]);
          keys = keys.concat(subKeys.map((subKey) => `${key}.${subKey}`));
        }
      }
    }

    return keys;
  }

  getMetoBlueStatus = () => {
    return [
      'clear',
      'cloudy',
      'changeable',
      'very_cloudy',
      'overcast',
      'foggy',
      'overcast_with_rain',
      'changeable_with_rain_showers',
      'thunderstorm',
      'overcast_with_snow',
      'changeable_with_snow_showers',
      'rainfall_mixed_with_snow',
    ];
  };

  getFieldActivitiesOPModule(
    farmsAppend: string,
    selectedYear: number
  ): Observable<FieldActivity[]> {
    const excludesList = ['machineActivity', 'machineActivities', 'equipmentActivities'];

    const searchParams = new URLSearchParams(farmsAppend);

    excludesList.forEach((e) => {
      searchParams.append('excludes', e);
    });

    searchParams.set('cropYear', '' + selectedYear);

    return this.http
      .get<FieldActivity[]>(this.activityServiceUrl + 'fieldActivities?' + searchParams.toString())
      .pipe(map((response) => response));
  }

  getFieldActivityExpandedById(id: number | string): Observable<FieldActivityGrouped> {
    return this.http.get<FieldActivity>(`${this.apiBaseUrl}fieldActivities/${id}`).pipe(
      map((response) => {
        const grouped = new FieldActivityGrouped();

        grouped.uuid = response.uuid;
        grouped.equipmentActivities = response.machineActivities;
        grouped.activities = [response];

        return grouped;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  newFilterForFieldActivities(
    fieldActivities: FieldActivity[],
    startDate: number,
    endDate: number,
    userIds: number[],
    fieldIds?: number[]
  ): FieldActivity[] {
    const hasUserFilter = userIds?.length > 0;
    const hasFieldFilter = fieldIds?.length > 0;
    const hasDateFilter = startDate > 0 && endDate > 0;

    const filtered = fieldActivities.filter((activity) => {
      if (hasUserFilter) {
        const hasMatchingUser = activity.users?.some((user) => userIds.includes(user.id));
        if (!hasMatchingUser) {
          return false;
        }
      }

      if (hasDateFilter) {
        const activityDay = moment(activity.activityDate).startOf('day').valueOf();
        if (
          activityDay < moment(startDate).startOf('day').valueOf() ||
          activityDay > moment(endDate).endOf('day').valueOf()
        ) {
          return false;
        }
      }

      if (hasFieldFilter) {
        return fieldIds!.includes(activity.field.id);
      }

      return true;
    });

    return Array.from(new Set(filtered));
  }

  checkIfProductIdExistInFieldActivity(productId: number): Array<FieldActivity> {
    const result: FieldActivity[] = [];

    this.fieldActivityList
      .pipe(first((list: FieldActivity[]) => !!list?.length))
      .subscribe((activities: FieldActivity[]) => {
        activities.forEach((activity) => {
          if (!activity?.mixes?.length) {
            return;
          }

          const hasProduct = activity.mixes.some((mix) => mix?.product?.id === productId);

          if (hasProduct && !result.includes(activity)) {
            result.push(activity);
          }
        });
      });

    return result;
  }

  getActivityForClone(fieldActivity: FieldActivity): FieldActivity {
    delete fieldActivity.machineActivities;
    delete fieldActivity.machineActivity;
    delete fieldActivity.isAutomatched;
    delete fieldActivity.automaticActions;
    delete fieldActivity.id;
    delete fieldActivity.uuid;

    fieldActivity.mixes?.forEach((mix) => {
      delete mix.fieldActivity;
      delete mix.uuid;
    });

    fieldActivity.fieldActivityItems?.forEach((item) => {
      delete item.fieldActivity;
      delete item.id;
    });

    return fieldActivity;
  }

  getFieldActivitiesCustomType() {
    return this.http
      .get<FieldActivitiesCustomType[]>(this.apiBaseUrl + 'fieldActivitiesCustomType')
      .pipe(
        map((response) => {
          const types = response.slice().sort((a, b) => a.customType.localeCompare(b.customType));

          this.fieldActivitiesCustomType$.next(types);
          return types;
        })
      );
  }

  saveFieldActivitiesCustomType(customType): Observable<any> {
    return this.http.post<FieldActivitiesCustomType>(
      `${this.apiBaseUrl}fieldActivitiesCustomType`,
      customType
    );
  }

  deleteFieldActivityCustomType(
    fieldActivityCustomType: FieldActivitiesCustomType
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.apiBaseUrl}fieldActivitiesCustomType/${fieldActivityCustomType.id}`
    );
  }

  deleteFieldActivityItem(id: number): Observable<FieldActivityItem> {
    const url = `${this.apiBaseUrl}fieldActivityItems/${id}`;

    return this.http.delete<void>(url).pipe(map(() => ({ id }) as FieldActivityItem));
  }

  //////////// PAGINATION///////////////

  getFieldActivitiesPaged(
    pageNumber: number,
    pageSize: number,
    year: number,
    donefilter: boolean | null,
    filterToApply = [],
    orderBy = null,
    textSearch = null
  ): Observable<PaginatedResponse<FieldActivity>> {
    // Read selected company from localStorage
    const companySelected = JSON.parse(localStorage.getItem('companySelected') ?? 'null');
    const companyId = companySelected?.id ?? null;

    // Add "done" filter only when explicitly provided
    const filtersList = [
      ...filterToApply,
      ...(donefilter != null ? [{ name: 'done', value: donefilter }] : []),
      {
        name: 'companyId',
        value: companyId,
      },
      {
        name: 'year',
        value: year,
      },
    ];

    // Use provided sorting or fallback to default by activity date
    const order = orderBy ?? [
      {
        property: 'activityDate',
        direction: 'DESC',
      },
    ];

    // Payload for paginated, filtered and sorted requests
    const filters = {
      number: pageNumber,
      max: pageSize,
      textSearch,
      sort: {
        orderBy: order,
      },
      filter: {
        filters: filtersList,
      },
    };

    return this.http.post<PaginatedResponse<FieldActivity>>(
      `${this.apiUrl}api/private/v2/fieldActivity?excludes=machineActivities`,
      filters
    );
  }

  getMostRecentActivityTypes(): Observable<string[]> {
    return this.http.get<string[]>(this.apiBaseUrl + 'activities/field/mostRecentTypes').pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching most recent activity types:', error);
        return of([]);
      })
    );
  }
}
